/*
 * Copyright (c) 2024, King Living
 * @override - Use local Field and useLoginFields with enhanced email validation
 */
import React from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import Field from '@amblique/king-living/app/components/field';
import {FieldLabelContent} from '@amblique/king-living/app/components/field/field-label-content';
import useLoginFields from '@amblique/king-living/app/components/forms/useLoginFields';
import {
  Box, Button, Input, Stack, Text, useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

const LoginFields = ({
  email,
  form,
  handleForgotPasswordClick,
  hideEmail = false,
  hidePassword = false,
  isEmailReadOnly = false,
  prefix = '',
}) => {
  const fields = useLoginFields({form, prefix});
  const styles = useMultiStyleConfig('FormAuth', {});

  return (
    <Stack className="login-fields-container" spacing={4}>
      {!hideEmail && (
        isEmailReadOnly && email ? (
          <Box {...styles.readOnlyEmailContainer}>
            <Text {...styles.readOnlyEmailLabel}>
              <FieldLabelContent
                label={
                  <FormattedMessage
                    defaultMessage="Email address"
                    id="login_form.label.email_address"
                  />
                }
                required
              />
            </Text>
            <Text {...styles.readOnlyEmailValue}>{email}</Text>
            <Input
              type="hidden"
              {...form.register('email')}
              value={email}
            />
          </Box>
        ) : (
          <Field {...fields.email} />
        )
      )}
      {!hidePassword && (
        <Stack spacing="16px">
          <Field
            {...fields.password}
            autoComplete="current-password"
            inputProps={{
              autoFocus: isEmailReadOnly,
            }}
          />
          {handleForgotPasswordClick && (
            <Box width="full">
              <Button
                type="button"
                variant="link"
                onClick={handleForgotPasswordClick}
                {...styles.buttonForgotPassword}
              >
                <FormattedMessage
                  defaultMessage="Forgot your password?"
                  id="login_form.link.forgot_password"
                />
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
};

LoginFields.propTypes = {
  /** Pre-filled email address */
  email: PropTypes.string,

  /** Object returned from `useForm` */
  form: PropTypes.object.isRequired,

  handleForgotPasswordClick: PropTypes.func,

  /** Optional configurations */
  hideEmail: PropTypes.bool,
  hidePassword: PropTypes.bool,

  /** Whether the email field should be read-only */
  isEmailReadOnly: PropTypes.bool,

  /** Optional prefix for field names */
  prefix: PropTypes.string,
};

export default LoginFields;
